import { ReactComponent as MagnifyingGlass } from "assets/svg/magnifying-glass.svg";
import { ReactComponent as OutlineLocation1 } from "assets/svg/outline-location-1.svg";
import { STATE_SEARCH_OPTIONS } from "components/utility/option-constants";
import { usePhonePortraitContext } from "contexts/phone-portrait-context";
import { useCallback } from "react";
import { Form, InputGroup } from "react-bootstrap";
import { AsyncTypeahead } from "react-bootstrap-typeahead";
import Select from "react-select";
import { AmplitudePageName, track } from "utils/amplitude";
import getClasses from "utils/getClasses";
import { SPECIALTY_OPTIONS, SPECIALTY_VALUE } from "../MarketPlace";
import styles from "./MarketPlaceHeader.module.scss";

const selectPlaceholderStyles = (styles) => ({
  ...styles,
  color: "#BDBDBD",
  fontWeight: 400,
});

const selectMenuStyles = (styles) => ({
  ...styles,
  fontSize: 14,
  zIndex: 5000,
  // TODO: check if there's better method
  outline: "1px solid white",
  filter: "drop-shadow(0px 2px 6px rgba(189, 189, 189, 0.3))",
});

const selectOptionStyles = (styles, { isSelected, isFocused }) => ({
  ...styles,
  backgroundColor: isSelected ? "#1778E1" : isFocused ? "#F3FAFF" : undefined,
  color: isSelected ? "#FFFFFF" : "#333333",
});

interface MarketPlaceHeaderProps {
  isGenericSeachHomePage: boolean;
  pageTitle: string;
  typeaheadOptions: {
    id: number;
    typeahead: string;
  }[];
  specialty: string;
  setSpecialty: React.Dispatch<React.SetStateAction<SPECIALTY_VALUE>>;
  state: string;
  setState: React.Dispatch<React.SetStateAction<string>>;
  zipcode: string;
  setZipcode: React.Dispatch<React.SetStateAction<string>>;
  setDoctorName: React.Dispatch<React.SetStateAction<string>>;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  handleSearchSubmit: (event: any) => void;
  amplitudePageName: AmplitudePageName;
}

const MarketPlaceHeader = ({
  isGenericSeachHomePage,
  pageTitle,
  typeaheadOptions,
  specialty,
  setSpecialty,
  state,
  setState,
  zipcode,
  setZipcode,
  setDoctorName,
  setPage,
  handleSearchSubmit,
  amplitudePageName,
}: MarketPlaceHeaderProps) => {
  const { isPhonePortrait } = usePhonePortraitContext();

  const handleZipcodeChange = useCallback(
    (e) => {
      setPage(1);
      setZipcode(e.target.value);
    },
    [setPage, setZipcode],
  );

  const handleOrchidProNameSearch = useCallback(
    (query) => {
      setPage(1);
      setDoctorName(query);
    },
    [setPage, setDoctorName],
  );

  return (
    <div
      className={getClasses(
        styles["pro-search"],
        isGenericSeachHomePage && styles["pro-search--generic"],
        isPhonePortrait && styles["pro-search--mobile"],
      )}
    >
      <h1
        className={getClasses(
          styles["pro-search__title"],
          isGenericSeachHomePage && styles["pro-search__title--generic"],
          isPhonePortrait && styles["pro-search__title--mobile"],
        )}
      >
        {pageTitle}
      </h1>
      <div
        className={getClasses(
          styles["pro-search__text"],
          isGenericSeachHomePage && styles["pro-search__text--generic"],
        )}
      >
        We're making it easier than ever to find the therapy that suits you.
        Book a one on one with one of our mental health professionals today.
      </div>
      <Form
        className={styles["pro-search__form"]}
        onSubmit={handleSearchSubmit}
      >
        <InputGroup
          className={getClasses(
            styles["pro-search__input-group"],
            isPhonePortrait && styles["pro-search__input-group--mobile"],
          )}
        >
          <Select
            isMulti={false}
            value={SPECIALTY_OPTIONS.find(
              (option) => option.value === specialty,
            )}
            options={SPECIALTY_OPTIONS}
            onChange={(el) => {
              setPage(1);
              setSpecialty(el?.value);
              track(
                amplitudePageName,
                "select-pro-type-dropdown-option",
                "click",
                { eventProperties: { providerType: el?.value } },
              );
            }}
            menuPlacement={"auto"}
            maxMenuHeight={200}
            isClearable={true}
            placeholder={"Provider Type"}
            components={{
              IndicatorSeparator: () => null,
            }}
            className={"smoothed"}
            styles={{
              control: (styles) => ({
                ...styles,
                minWidth: 236,
                borderRight: "none",
                borderTopRightRadius: 0,
                borderBottomRightRadius: 0,
                backgroundColor: "white",
                borderColor: "#ADCCFF",
                fontSize: 16,
                color: "#828282",
                boxShadow: "none",
                minHeight: 60,
              }),
              placeholder: selectPlaceholderStyles,
              menu: selectMenuStyles,
              option: selectOptionStyles,
            }}
          />
          <div className={styles["input-with-label"]}>
            <InputGroup.Text className={styles["pro-search__input-icon"]}>
              <MagnifyingGlass />
            </InputGroup.Text>
            <AsyncTypeahead
              minLength={0}
              labelKey={"typeahead"}
              isLoading={false}
              id="async-typeahead"
              onChange={(s) => handleOrchidProNameSearch(s[0]?.typeahead || "")}
              onInputChange={(v) => {
                // only need this to query when there is empty string
                if (!v) {
                  handleOrchidProNameSearch("");
                }
              }}
              onSearch={handleOrchidProNameSearch}
              placeholder={"Provider name, Focus issue, Treatment..."}
              options={typeaheadOptions}
              useCache={false}
              onFocus={() =>
                track(
                  amplitudePageName,
                  "search-professional-name-field",
                  "focus",
                )
              }
              className={styles["pro-search__input"]}
            />
          </div>
          <div className={styles["input-with-label"]}>
            <InputGroup.Text className={styles["pro-search__input-icon"]}>
              <OutlineLocation1 />
            </InputGroup.Text>
            <Select
              isMulti={false}
              value={STATE_SEARCH_OPTIONS.find(
                (option) => option.value === state,
              )}
              options={STATE_SEARCH_OPTIONS}
              onChange={(el) => {
                setPage(1);
                setState(el?.value);
                track(
                  amplitudePageName,
                  "select-state-dropdown-option",
                  "click",
                  { eventProperties: { state: el?.value } },
                );
              }}
              menuPlacement={"auto"}
              maxMenuHeight={200}
              isClearable={true}
              placeholder={"Select State"}
              components={{
                IndicatorSeparator: () => null,
              }}
              className={"smoothed w-100"}
              styles={{
                control: (styles) => ({
                  ...styles,
                  minWidth: 236,
                  borderLeft: "none",
                  borderRight: "none",
                  borderRadius: 0,
                  backgroundColor: "white",
                  borderColor: "#ADCCFF",
                  fontSize: 16,
                  color: "#828282",
                  boxShadow: "none",
                  minHeight: 60,

                  "&:hover": {
                    borderColor: "#ADCCFF",
                  },
                }),

                placeholder: selectPlaceholderStyles,
                menu: selectMenuStyles,
                option: selectOptionStyles,
              }}
            />
          </div>
          <Form.Control
            name="zipcode"
            value={zipcode}
            onChange={handleZipcodeChange}
            placeholder="Zipcode"
            onFocus={() => track(amplitudePageName, "zip-code-field", "focus")}
            className={styles["zipcode"]}
          />
          {/* <InputGroup.Append>
                <Button
                  className={styles["pro-search__action"]}
                  onClick={() => {}}
                >
                  <OutlineSearch44 />
                </Button>
              </InputGroup.Append> */}
        </InputGroup>
      </Form>
    </div>
  );
};

export default MarketPlaceHeader;
